import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { createChoiceList } from '@mkcore/platform/schema';
import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  Mono,
  PageHeader,
  Skeleton,
  Textarea,
} from '@mkcore/ui';
import { newIdempotencyKey } from '@/lib/api';
import { useActionMutation } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { choiceListsQuery } from '@/features/admin/queries';

export const Route = createFileRoute('/_app/admin/choice-lists/')({ component: ChoiceLists });

/** Manageable choice lists (CO-0039). */
function ChoiceLists() {
  const { t } = useTranslation();
  const lists = useQuery(choiceListsQuery);
  const canCreate = useCan('admin:create');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ key: '', name: '', values: '' });
  const [key] = useState(newIdempotencyKey);
  const create = useActionMutation(createChoiceList, {
    invalidate: [['admin', 'choiceList']],
    onSuccess: (l) => void navigate({ to: '/admin/choice-lists/$id', params: { id: l.id } }),
  });
  return (
    <>
      <PageHeader
        eyebrow={t('nav.admin')}
        title={t('choiceLists.title')}
        description={t('choiceLists.description')}
        actions={
          canCreate && (
            <Button onClick={() => setOpen(true)}>
              <Plus /> {t('choiceLists.new')}
            </Button>
          )
        }
      />
      {!lists.data ? (
        <Skeleton className="h-48" />
      ) : !lists.data.items.length ? (
        <EmptyState title={t('choiceLists.title')} />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {lists.data.items.map((l) => (
            <Link key={l.id} to="/admin/choice-lists/$id" params={{ id: l.id }}>
              <Card className="h-full p-4 transition-colors hover:border-gold">
                <div className="font-bold">{l.name}</div>
                <Mono className="text-xs">{l.key}</Mono>
                <p className="mt-2 line-clamp-2 text-sm text-fg-2">
                  {l.values
                    .filter((v) => v.active)
                    .map((v) => v.label)
                    .join(', ')}
                </p>
              </Card>
            </Link>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent closeLabel={t('common.close')}>
          <DialogHeader>
            <DialogTitle>{t('choiceLists.new')}</DialogTitle>
          </DialogHeader>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate({
                key,
                input: {
                  key: form.key,
                  name: form.name,
                  values: form.values
                    .split(/\r?\n/)
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .map((label) => ({ label })),
                },
              });
            }}
          >
            <Field label={t('common.name')}>{(p) => <Input {...p} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />}</Field>
            <Field label={t('choiceLists.key')} hint="lead.source">
              {(p) => <Input {...p} required pattern="[a-z][a-zA-Z0-9.]*" className="font-mono" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} />}
            </Field>
            <Field label={t('choiceLists.values')} hint={t('choiceLists.pasteHint')}>
              {(p) => <Textarea {...p} value={form.values} onChange={(e) => setForm({ ...form, values: e.target.value })} />}
            </Field>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" loading={create.isPending}>
                {t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
